import React, { useState, useEffect } from 'react';
import { Box, Modal, Typography, Button, ButtonGroup, TextField, Divider, CircularProgress } from '@mui/material';
import { getParameters } from '../libs/BitmapEditor/bitmapMaker3';
import { getCreateWith } from '../helpers/createWith';

// Extend Window interface to include displaySketch
declare global {
	interface Window {
		displaySketch: any;
	}
}

export type SaveModalProps = {
	open: boolean;
	onClose: () => void;
};

const PRESET_SCALES = [1, 2, 3, 4];

function SaveModal({ open, onClose }: SaveModalProps) {
	const [selectedScale, setSelectedScale] = useState<number>(1);
	const [customScale, setCustomScale] = useState<string>('');
	const [isCustom, setIsCustom] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const createWith = getCreateWith();
	const dpSketch = () => window.displaySketch;

	// Get canvas dimensions from parameters
	const parameters = getParameters();
	const baseWidth = parameters.width;
	const baseHeight = parameters.height;

	// Calculate final dimensions
	const scale = isCustom ? (customScale === '' ? 0 : parseFloat(customScale) || 1) : selectedScale;
	const finalWidth = Math.round(baseWidth * scale);
	const finalHeight = Math.round(baseHeight * scale);

	// Estimate file size (very rough approximation for PNG)
	const estimateFileSizeKB = Math.round((finalWidth * finalHeight * 4) / 1024); // 4 bytes per pixel for RGBA
	const formatFileSize = (sizeKB: number): string => {
		if (sizeKB < 1024) {
			return `~${sizeKB} KB`;
		} else {
			return `~${(sizeKB / 1024).toFixed(1)} MB`;
		}
	};

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setSelectedScale(1);
			setCustomScale('');
			setIsCustom(false);
			setIsSaving(false);
		}
	}, [open]);

	const handlePresetSelect = (preset: number) => {
		setSelectedScale(preset);
		setIsCustom(false);
		setCustomScale('');
	};

	const handleCustomToggle = () => {
		setIsCustom(!isCustom);
		if (!isCustom) {
			setCustomScale(selectedScale.toString());
		}
	};

	const handleCustomScaleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		// Allow empty string or positive numbers with up to 2 decimal places
		if (value === '' || (/^\d*\.?\d{0,2}$/.test(value) && (value === '' || parseFloat(value) <= 20))) {
			setCustomScale(value);
		}
	};

	const generateScaledCanvas = (): HTMLCanvasElement => {
		const sourceCanvas = dpSketch()?.canvas as HTMLCanvasElement;
		if (!sourceCanvas) {
			throw new Error('Source canvas not found');
		}

		// Create a new canvas for the scaled version
		const scaledCanvas = document.createElement('canvas');
		scaledCanvas.width = finalWidth;
		scaledCanvas.height = finalHeight;

		const ctx = scaledCanvas.getContext('2d');
		if (!ctx) {
			throw new Error('Could not get 2D context');
		}

		// Disable image smoothing for pixel-perfect scaling
		ctx.imageSmoothingEnabled = false;
		// @ts-ignore - Webkit prefix
		ctx.webkitImageSmoothingEnabled = false;
		// @ts-ignore - Mozilla prefix
		ctx.mozImageSmoothingEnabled = false;
		// @ts-ignore - Microsoft prefix
		ctx.msImageSmoothingEnabled = false;

		// Draw the source canvas scaled up to the new canvas
		ctx.drawImage(sourceCanvas, 0, 0, baseWidth, baseHeight, 0, 0, finalWidth, finalHeight);

		return scaledCanvas;
	};

	const handleSave = async () => {
		if (!createWith || isSaving) return;

		try {
			setIsSaving(true);

			const scaledCanvas = generateScaledCanvas();
			const canvasData = scaledCanvas.toDataURL('image/png');

			await createWith.local_save(canvasData);
			onClose();
		} catch (error: any) {
			console.error('Error saving scaled image:', error);
			alert('Error saving image to local device.');
		} finally {
			setIsSaving(false);
		}
	};

	const isValidScale = scale > 0 && scale <= 20 && (!isCustom || customScale !== '');

	return (
		<Modal open={open} onClose={onClose} aria-labelledby="save-modal-title" aria-describedby="save-modal-description">
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
					width: '90%',
					maxWidth: 500,
					bgcolor: 'background.paper',
					border: '1px solid #000',
					boxShadow: 24,
					borderRadius: 3,
					p: 3,
				}}
			>
				<Typography id="save-modal-title" variant="h6" component="h2" align="center" fontWeight={700} mb={2}>
					Save to Device
				</Typography>

				<Divider variant="middle" light sx={{ mb: 3 }} />

				{/* Original Size Info */}
				<Box mb={3}>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>
						Original Size: {baseWidth} × {baseHeight} pixels
					</Typography>
				</Box>

				{/* Scale Selection */}
				<Box mb={3}>
					<Typography variant="subtitle1" gutterBottom fontWeight={600}>
						Select Scale:
					</Typography>

					<ButtonGroup variant="outlined" sx={{ mb: 2, flexWrap: 'wrap' }}>
						{PRESET_SCALES.map(preset => (
							<Button key={preset} variant={!isCustom && selectedScale === preset ? 'contained' : 'outlined'} onClick={() => handlePresetSelect(preset)} sx={{ minWidth: '60px' }}>
								{preset}x
							</Button>
						))}
						<Button variant={isCustom ? 'contained' : 'outlined'} onClick={handleCustomToggle} sx={{ minWidth: '80px' }}>
							Custom
						</Button>
					</ButtonGroup>

					{isCustom && (
						<TextField
							label="Custom Scale"
							value={customScale}
							onChange={handleCustomScaleChange}
							placeholder="e.g. 1.5"
							size="small"
							sx={{ width: '150px' }}
							helperText="(0.1 - 20x)"
							error={isCustom && customScale !== '' && !isValidScale}
						/>
					)}
				</Box>

				{/* Final Dimensions Preview */}
				<Box mb={3}>
					<Typography variant="subtitle1" fontWeight={600} gutterBottom>
						Final Dimensions:
					</Typography>
					{isCustom && customScale === '' ? (
						<Typography variant="h6" color="text.main">
							Enter scale to see dimensions
						</Typography>
					) : (
						<>
							<Typography variant="h6" color="primary.main">
								{finalWidth} × {finalHeight} pixels
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{scale}x scale factor • {formatFileSize(estimateFileSizeKB)}
							</Typography>
						</>
					)}
				</Box>

				<Divider variant="middle" light sx={{ mb: 3 }} />

				{/* Action Buttons */}
				<Box display="flex" gap={2} justifyContent="flex-end">
					<Button variant="outlined" onClick={onClose} disabled={isSaving}>
						Cancel
					</Button>
					<Button variant="contained" onClick={handleSave} disabled={!isValidScale || isSaving} startIcon={isSaving ? <CircularProgress size={16} /> : null} sx={{ minWidth: '120px' }}>
						{isSaving ? 'Saving...' : 'Save Image'}
					</Button>
				</Box>
			</Box>
		</Modal>
	);
}

export default SaveModal;
